import type { ErrorDetail } from '@dayon/design-system';

/**
 * Download a stored report artefact.
 *
 * NOT through `lib/api.ts`: that helper reads a JSON envelope, and this
 * response is a CSV file. The fetch is written out here so the failure path can
 * still read an envelope — a refusal (no permission, run not finished, out of
 * brand scope) comes back as JSON with the reason, and the operator must see
 * that reason in the error modal rather than have the browser save a file
 * containing an error message.
 *
 * The file comes from `report_artefact`, never from re-running the report. Two
 * people downloading "the January extract" a week apart get identical bytes,
 * which is the half of "immutable once generated" the schema can actually keep.
 *
 * ── THE FORMAT IS THE CALLER'S, AND CSV IS STILL THE DEFAULT ───────────────
 *
 * PAGCOR item 34 lets the regulator name the format. A PDF of a run is RENDERED
 * FROM THE STORED CSV rather than from a second query, so the two files cannot
 * disagree about what the extract contained — and the first PDF download is kept
 * beside the CSV, so the same run gives the same bytes afterwards.
 */
export async function downloadRun(
  ref: string,
  showError: (detail: ErrorDetail) => void,
  toast: (message: string) => void,
  format: 'CSV' | 'PDF' = 'CSV',
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(
      `/api/reports/runs/${encodeURIComponent(ref)}/download` +
        (format === 'CSV' ? '' : `?format=${format}`),
      { credentials: 'same-origin' },
    );
  } catch {
    showError({
      title: 'Could not download the report',
      message: 'Could not reach the platform. Check your connection and try again.',
      code: 'NETWORK',
    });
    return;
  }

  if (!response.ok) {
    // A refusal still arrives in the envelope, so the real reason is shown
    // rather than a bare status code.
    let message = `The platform refused the download (HTTP ${response.status}).`;
    let code: string | undefined;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      if (body.error?.message) message = body.error.message;
      code = body.error?.code;
    } catch {
      /* Not an envelope. The status-based sentence above stands. */
    }
    showError({ title: 'Could not download the report', message, ...(code ? { code } : {}) });
    return;
  }

  const blob = await response.blob();
  // The server names the file — it knows the report, the window and the run
  // reference. Falling back to the ref alone rather than inventing a name.
  const disposition = response.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition);
  const filename = named?.[1] ?? `${ref}.${format.toLowerCase()}`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);

  toast(`${filename} saved.`);
}
